import * as THREE from 'three';
import { calculatePlanesIntersectionLine } from '@/engine/utils/geometryHelper';
import { TYPES, iocContainer } from '../ioc';
import type { CaseDataProvider } from '../app/CaseDataProvider';
import { DecodeDracoToBufferGeometry } from '../Utilities/DracoUtils';

export async function loadDracoAsBufferGeometry(blobDescriptor: string): Promise<THREE.BufferGeometry> {
  if (blobDescriptor == '<empty>') {
    return new THREE.BufferGeometry();
  }
  const caseDataProvider = iocContainer.get<CaseDataProvider>(TYPES.CaseDataProvider);
  const buffer = await caseDataProvider.getCaseBlob(blobDescriptor + '.drc');
  return DecodeDracoToBufferGeometry(new Uint8Array(buffer));
}

export function createBufferGeometryFromPointsCollection(
  pts: Array<THREE.Vector3>,
  isClosed: boolean
): THREE.BufferGeometry {
  const coordinates = new Float32Array((isClosed ? pts.length + 1 : pts.length) * 3);
  for (let i = 0, cnt = pts.length; i < cnt; ++i) {
    pts[i].toArray(coordinates, i * 3);
  }
  if (isClosed) {
    pts[0].toArray(coordinates, coordinates.length - 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(coordinates, 3));
  return geometry;
}

function _makeTransformationRaw(
  ox: number,
  oy: number,
  oz: number,
  xx: number,
  xy: number,
  xz: number,
  yx: number,
  yy: number,
  yz: number,
  zx: number,
  zy: number,
  zz: number
): Array<number> {
  // if x,y,z is a point expressed in the coordinate system of which vecX,vecY,vecZ are the axes and origin is the origin, then
  // vecX*x+vecY*y+vecZ*z+origin is the same point expressed in the base coordinate system.

  const matrix = new Array<number>(16);

  matrix[0] = xx;
  matrix[4] = yx;
  matrix[8] = zx;
  matrix[12] = ox;

  matrix[1] = xy;
  matrix[5] = yy;
  matrix[9] = zy;
  matrix[13] = oy;

  matrix[2] = xz;
  matrix[6] = yz;
  matrix[10] = zz;
  matrix[14] = oz;

  matrix[3] = 0;
  matrix[7] = 0;
  matrix[11] = 0;
  matrix[15] = 1;

  return matrix;
}

export function makeTransformation(
  origin: THREE.Vector3,
  vecX: THREE.Vector3,
  vecY: THREE.Vector3,
  vecZ: THREE.Vector3
): THREE.Matrix4 {
  const elements = _makeTransformationRaw(
    origin.x,
    origin.y,
    origin.z,
    vecX.x,
    vecX.y,
    vecX.z,
    vecY.x,
    vecY.y,
    vecY.z,
    vecZ.x,
    vecZ.y,
    vecZ.z
  );
  return new THREE.Matrix4().fromArray(elements);
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class _inscribedSectorsDetails {
  public static radiusVectorAngle(pt: THREE.Vector2): number {
    const val = Math.atan2(pt.y, pt.x);
    return val >= 0 ? val : 2 * Math.PI + val; // [-PI..PI] => [0..2PI]
  }

  public static normalizePoints(
    pt1: THREE.Vector2,
    pt2: THREE.Vector2
  ): {
    from: { point: THREE.Vector2; angle: number };
    to: { point: THREE.Vector2; angle: number };
  } {
    const ang1 = _inscribedSectorsDetails.radiusVectorAngle(pt1);
    const ang2 = _inscribedSectorsDetails.radiusVectorAngle(pt2);
    const dataMin = ang1 <= ang2 ? { point: pt1, angle: ang1 } : { point: pt2, angle: ang2 };
    const dataMax = ang1 <= ang2 ? { point: pt2, angle: ang2 } : { point: pt1, angle: ang1 };
    // Check if segment crosses Q4-Q1 border (positive x-axis)
    const crossesTwoPi = dataMax.angle > Math.PI && dataMax.angle - dataMin.angle > Math.PI;
    if (crossesTwoPi) {
      dataMin.angle += 2 * Math.PI;
      return { from: dataMax, to: dataMin };
    } else {
      return { from: dataMin, to: dataMax };
    }
  }

  public static getAngleSectorIndex(angle: number, sectorSize: number): number {
    // [0..3PI) - Q1-Q2-Q3-Q4-Q1-Q2 in case of full circle, when segment starting point is in Q4 but ending point is in Q2
    if (angle < 0 || angle >= 3 * Math.PI) throw new Error('Angle must be [0..3PI)');
    return Math.max(0, Math.round(angle / sectorSize - 0.5));
  }

  public static getIntersectingSectorsIndices(angleFrom: number, angleTo: number, sectorSize: number): Array<number> {
    if (angleFrom > angleTo) throw new Error('angleFrom must be smaller or equal to angleTo');
    const ret: Array<number> = [];
    const idxFrom = _inscribedSectorsDetails.getAngleSectorIndex(angleFrom, sectorSize);
    const idxTo = _inscribedSectorsDetails.getAngleSectorIndex(angleTo, sectorSize);
    if (idxFrom == idxTo) return ret;
    for (let i = idxFrom + 1; i <= idxTo; ++i) ret.push(i);
    return ret;
  }

  public static getSectorIntersectionDistance(ptFrom: THREE.Vector2, ptTo: THREE.Vector2, sectorAngle: number): number {
    // rotate ptFrom and ptTo points in such away that sector border-line becomes X-axis
    const cos = Math.cos(-sectorAngle);
    const sin = Math.sin(-sectorAngle);
    const x1 = cos * ptFrom.x - sin * ptFrom.y;
    const y1 = sin * ptFrom.x + cos * ptFrom.y;
    const x2 = cos * ptTo.x - sin * ptTo.y;
    const y2 = sin * ptTo.x + cos * ptTo.y;
    // find an intersection point's X coordinate, Y coordinate is 0
    return x2 - (y2 * (x2 - x1)) / (y2 - y1);
  }

  public static getSectorIntersectionPoint(intersectionDistance: number, sectorAngle: number): THREE.Vector2 {
    return new THREE.Vector2(
      Math.cos(sectorAngle) * intersectionDistance,
      Math.sin(sectorAngle) * intersectionDistance
    );
  }
}

export function centroid(points: Array<THREE.Vector3>): THREE.Vector3 {
  // Ported from MatSDK by [chebotarev]
  const origin = new THREE.Vector3(0, 0, 0);
  for (const pt of points) origin.add(pt);
  return origin.divideScalar(points.length);
}

export function unitNormal(origin: THREE.Vector3, points: Array<THREE.Vector3>, closed: boolean): THREE.Vector3 {
  // Ported from MatSDK by [chebotarev]
  const normal = new THREE.Vector3(0, 0, 0);
  const num = closed ? points.length : points.length - 1;
  const firstIdx = closed ? points.length - 1 : 0;
  const secondIdx = closed ? 0 : 1;
  let vec3d = points[firstIdx].clone().sub(origin);
  for (let i = secondIdx; i < num; ++i) {
    const vec3dPrev = vec3d;
    vec3d = points[i].clone().sub(origin);
    normal.add(vec3d.clone().cross(vec3dPrev)); // [chebotarev] this order is crucial
  }
  return normal.divideScalar(points.length);
}

export function contour2dParams(contour2d: Array<THREE.Vector2>): {
  signedArea: number;
  perimeter: number;
  center: THREE.Vector2;
} {
  // Ported from MatSDK by [chebotarev]
  let signedArea = 0;
  let perimeter = 0;
  let nomX = 0;
  let nomY = 0;
  let denX = 0;
  let denY = 0;
  let pt1 = contour2d[contour2d.length - 1];
  for (const pt2 of contour2d) {
    // SurfaceAreaWithSignXY
    if (pt2.x != pt1.x) signedArea += ((pt2.y + pt1.y) * (pt2.x - pt1.x)) / 2;
    // CircumferenceXY
    const dX = pt2.x - pt1.x;
    const dY = pt2.y - pt1.y;
    perimeter += Math.sqrt(dX * dX + dY * dY);
    // CalculateContourCenter2d
    nomX += ((pt1.x - pt2.x) * pt1.y * (pt1.x + pt2.x)) / 2;
    nomX += ((pt2.y - pt1.y) * (pt1.x - pt2.x) * (pt2.x + 2 * pt1.x)) / 6;
    denX += (pt1.x - pt2.x) * pt1.y;
    denX += ((pt2.y - pt1.y) * (pt1.x - pt2.x)) / 2;
    nomY += ((pt2.y - pt1.y) * pt2.x * (pt2.y + pt1.y)) / 2;
    nomY += ((pt1.x - pt2.x) * (pt2.y - pt1.y) * (pt1.y + 2 * pt2.y)) / 6;
    denY += (pt2.y - pt1.y) * pt2.x;
    denY += ((pt1.x - pt2.x) * (pt2.y - pt1.y)) / 2;
    pt1 = pt2;
  }
  return {
    signedArea: signedArea,
    perimeter: perimeter,
    center: new THREE.Vector2(nomX / denX, nomY / denY)
  };
}

export function sliceParams(polyline: { points: Array<THREE.Vector3>; isClosed: boolean }):
  | {
      signedArea: number;
      perimeter: number;
      center: THREE.Vector3;
      normal: THREE.Vector3;
    }
  | undefined {
  if (polyline.points.length < 3) {
    console.warn('Contour is invalid');
    return undefined;
  }

  if (!polyline.isClosed) {
    console.warn('Contour is not closed');
  }

  const center = centroid(polyline.points);

  const normal = unitNormal(center, polyline.points, polyline.isClosed).normalize();

  const rotation = new THREE.Quaternion().setFromUnitVectors(normal, new THREE.Vector3(0, 0, 1));
  const translation = new THREE.Matrix4().makeTranslation(0, 0, -center.clone().applyQuaternion(rotation).z);
  const from3dTo2d = new THREE.Matrix4().makeRotationFromQuaternion(rotation).premultiply(translation);
  const from2dTo3d = from3dTo2d.clone().invert();

  const polyline2d = new Array<THREE.Vector2>(polyline.points.length);
  for (let j = 0; j < polyline.points.length; ++j) {
    polyline2d[j] = new THREE.Vector2().fromArray(polyline.points[j].clone().applyMatrix4(from3dTo2d).toArray());
  }

  const params = contour2dParams(polyline2d);

  return {
    signedArea: params.signedArea,
    perimeter: params.perimeter,
    center: new THREE.Vector3(params.center.x, params.center.y, 0).applyMatrix4(from2dTo3d),
    normal: normal
  };
}

export function inscribedSectors(
  polyline2d: Array<THREE.Vector2>,
  center2d: THREE.Vector2,
  numSectorsInQuarter: number
): Array<THREE.Vector2> {
  const numSectors = numSectorsInQuarter * 4;
  const sectorSize = (Math.PI * 2) / numSectors;
  const sectorsData = new Array<number>(numSectors).fill(-1);
  let ptPrev = polyline2d[polyline2d.length - 1].clone().sub(center2d);
  for (const _ptCurr of polyline2d) {
    const ptCurr = _ptCurr.clone().sub(center2d);
    const pts = _inscribedSectorsDetails.normalizePoints(ptPrev, ptCurr);
    const intSectors = _inscribedSectorsDetails.getIntersectingSectorsIndices(pts.from.angle, pts.to.angle, sectorSize);
    for (const sectorIndex of intSectors) {
      const dist = _inscribedSectorsDetails.getSectorIntersectionDistance(
        pts.from.point,
        pts.to.point,
        sectorIndex * sectorSize
      );
      if (dist < 0) throw new Error('Sector Intersection Distance expected to be always positive.');
      const idx = sectorIndex < numSectors ? sectorIndex : sectorIndex - numSectors;
      const found = sectorsData[idx];
      if (found < 0 || found > dist) {
        sectorsData[idx] = dist;
      }
    }
    ptPrev = ptCurr;
  }
  if (sectorsData.find((dist: number) => dist < 0) !== undefined) return [];
  return sectorsData.map((dist: number, idx: number) =>
    _inscribedSectorsDetails.getSectorIntersectionPoint(dist, idx * sectorSize).add(center2d)
  );
}

export function intersectProjectedContourWithPlane(
  contour3d: Array<THREE.Vector3>,
  projectionPlane: THREE.Plane,
  intersectionPlane: THREE.Plane
): Array<[THREE.Vector3, THREE.Vector3]> {
  const ret = new Array<[THREE.Vector3, THREE.Vector3]>();

  const intersection = calculatePlanesIntersectionLine(projectionPlane, intersectionPlane, 1e-2);
  if (!intersection) {
    return ret;
  }

  const vecZ = projectionPlane.normal.clone().normalize();
  const vecX = intersection.direction.clone().normalize();
  const vecY = new THREE.Vector3().crossVectors(vecZ, vecX).normalize();
  const origin = intersection.origin.clone();
  const from2dTo3d = makeTransformation(origin, vecX, vecY, vecZ);
  const from3dTo2d = from2dTo3d.clone().invert();

  const xIntersections = new Array<number>();
  let ptPrev = contour3d[contour3d.length - 1].clone().applyMatrix4(from3dTo2d);
  for (const _ptCurr of contour3d) {
    const ptCurr = _ptCurr.clone().applyMatrix4(from3dTo2d);
    if (Math.abs(ptPrev.y) >= 1e-2 && Math.abs(ptCurr.y) >= 1e-2) {
      if (Math.sign(ptPrev.y) != Math.sign(ptCurr.y)) {
        // if X axis is just crossed
        // find an intersection point's X coordinate, Y coordinate is 0
        xIntersections.push(ptCurr.x - (ptCurr.y * (ptCurr.x - ptPrev.x)) / (ptCurr.y - ptPrev.y));
      }
    } else {
      if (Math.abs(ptCurr.y) < 1e-2) {
        xIntersections.push(ptCurr.x);
      }
    }
    ptPrev = ptCurr;
  }
  if (xIntersections.length > 1) {
    const z2d = origin.clone().applyMatrix4(from3dTo2d).z;
    xIntersections.sort((a, b) => a - b);
    if (xIntersections.length % 2 != 0) {
      console.warn('Ambiguous intersections');
      ret.push([
        new THREE.Vector3(xIntersections[0], 0, z2d).applyMatrix4(from2dTo3d),
        new THREE.Vector3(xIntersections[xIntersections.length - 1], 0, z2d).applyMatrix4(from2dTo3d)
      ]);
    } else {
      for (let i = 0; i < xIntersections.length; i += 2) {
        ret.push([
          new THREE.Vector3(xIntersections[i], 0, z2d).applyMatrix4(from2dTo3d),
          new THREE.Vector3(xIntersections[i + 1], 0, z2d).applyMatrix4(from2dTo3d)
        ]);
      }
    }
  }
  return ret;
}

export function coronalOrientationFromDirection(direction: THREE.Vector3): {
  orientationZ: THREE.Vector3;
  orientationY: THREE.Vector3;
} {
  const orientationZ = direction;
  const align = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), orientationZ);
  const basisX = new THREE.Vector3(1, 0, 0);
  const vecXFrom = basisX.clone().applyQuaternion(align).normalize();
  const vecXTo = orientationZ.clone().cross(basisX).cross(orientationZ).normalize();
  const alignY = new THREE.Quaternion().setFromUnitVectors(vecXFrom, vecXTo);
  align.premultiply(alignY);
  const orientationY = new THREE.Vector3(0, -1, 0).applyQuaternion(align);
  return {
    orientationZ: orientationZ,
    orientationY: orientationY
  };
}

export function findSplineSectorPoint(
  splinePoints: Array<THREE.Vector3>,
  splineCenter: THREE.Vector3,
  splineNormal: THREE.Vector3,
  initialVector: THREE.Vector3
): THREE.Vector3 {
  const pointVec = initialVector.clone().cross(splineNormal).negate().cross(splineNormal).normalize();

  let prevPt = splinePoints[splinePoints.length - 1];
  let prevVec = prevPt.clone().sub(splineCenter);
  prevVec.cross(splineNormal).negate().cross(splineNormal).normalize();
  let prevCodirNormal = pointVec.dot(prevVec) > 0 ? pointVec.clone().cross(prevVec) : undefined;
  for (const currPt of splinePoints) {
    const currVec = currPt.clone().sub(splineCenter);
    currVec.cross(splineNormal).negate().cross(splineNormal).normalize();
    if (currVec.angleTo(pointVec) < 1e-4) {
      return currPt.clone();
    }

    const currCodirNormal = pointVec.dot(currVec) > 0 ? pointVec.clone().cross(currVec) : undefined;

    if (currPt.distanceToSquared(prevPt) > 1e-4) {
      if (prevCodirNormal && currCodirNormal && prevCodirNormal.dot(currCodirNormal) < 0) {
        const pl = new THREE.Plane().setFromNormalAndCoplanarPoint(
          pointVec.clone().cross(splineNormal).normalize(),
          splineCenter
        );
        const ln = new THREE.Line3(prevPt, currPt);
        const pt = pl.intersectLine(ln, new THREE.Vector3());
        if (pt) {
          return pt;
        }
        break;
      }
    } else {
      console.warn('Points are too close to each other');
    }

    prevPt = currPt;
    prevVec = currVec;
    prevCodirNormal = currCodirNormal;
  }

  console.warn('Commissure initial point not found');
  return splineCenter.clone().add(pointVec);
}

export function getSplineDistanceToRayProjected(
  splinePts: Array<THREE.Vector3>,
  ray: THREE.Ray,
  normal: THREE.Vector3
): { distance: number; pointIndex: number } {
  const ret = { distance: 0, pointIndex: -1 };

  // construct coordinate system transformation to make all the spline points
  //   located in such a way, that distance from point to the ray is Y coordinate
  const orientationZ = normal.clone().normalize();
  const orientationY = orientationZ.clone().cross(ray.direction).normalize();
  const orientationX = orientationY.clone().cross(orientationZ).normalize();
  const transformTo = makeTransformation(ray.origin, orientationX, orientationY, orientationZ).invert();

  // perform transformation on each spline point and find extrema
  //   (we need both min and max as we don't know yet if ray intersects the spline)
  let minPtIdx = -1;
  let maxPtIdx = -1;
  let minDist = Number.MAX_SAFE_INTEGER;
  let maxDist = Number.MIN_SAFE_INTEGER;
  for (let i = 0, sz = splinePts.length; i < sz; ++i) {
    const dist = splinePts[i].clone().applyMatrix4(transformTo).y;
    if (dist < minDist) {
      minPtIdx = i;
      minDist = dist;
    }
    if (dist > maxDist) {
      maxPtIdx = i;
      maxDist = dist;
    }
  }

  if (minPtIdx == -1 || maxPtIdx == -1) {
    return ret;
  }

  // we are interested in absolute extrema values
  const minDistAbs = Math.abs(minDist);
  const maxDistAbs = Math.abs(maxDist);

  if (Math.sign(minDist) == Math.sign(maxDist)) {
    // ray is not intersecting the spline: all spline points are from one side of the ray
    //   thus we need min abs value
    if (minDistAbs <= maxDistAbs) {
      ret.distance = minDist;
      ret.pointIndex = minPtIdx;
    } else {
      ret.distance = maxDist;
      ret.pointIndex = maxPtIdx;
    }
  }

  // ray is intersecting the spline: spline points are from both side of the ray
  //   thus no distance

  return ret;
}
